// @flow

import React, {type ComponentType, type AbstractComponent} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {type StoreShapeType} from 'app/reducers/types';

/*
    this enhancer should load object for the page using the hashid
    provided in the route URL and inject useful props to the wrapped
    component:

    It will injected the 'fetchedByHashid' prop into decorated component with
    this format:

    props: {
        fetchedByHashid: {
            object: fetched object,
            fetch: () => function to refetch the object and update the 'object'
                         prop. No parameters required.
        }
    }

    While the object is being loaded, additional props will be injected
    with loading state:

    props: {
        isLoading: flag to indicate if object is still being loaded,
        error: exception caught while loading the object. It's undefined
               if the fetch was successful

    }

    If loadingComponent parameter is used, it's value will be rendered as
    component while the data is being loaded
 */

export type LoadByHashidInjectedProps<T> = {|
  fetchedByHashid: {
    object: T,
    fetchObject: () => Promise<any>,
  },
|};

export default function loadByHashid(
  fetchByHashidAction: ({hashid: string}) => (any) => Promise<any>,
  storeMap: [string, Array<string>] | ((StoreShapeType) => any),
  LoadingComponent: ComponentType<any>,
  denormalizeFuncFactory?: (StoreShapeType, any) => (any) => any = () =>
    (obj) =>
      obj
) {
  type RequiredProps = {+params: {+hashid: string}};

  return function <Config: RequiredProps, T: {id: number}>(
    WrappedComponent: AbstractComponent<{|
      ...$Exact<Config>,
      ...LoadByHashidInjectedProps<T>,
    |}>
  ): AbstractComponent<Config> {
    const genericStoreFetchSelector = createSelector<
      [string, Array<string>] | ((StoreShapeType) => any),
      string,
      void | T,
      string,
      any,
      (T) => T
    >(
      (storeMap, hashid) => hashid,
      (storeMap, _, state) => {
        if (typeof storeMap === 'function') {
          return storeMap(state);
        }
        const [storeKey, storePath] = storeMap;
        return state[storeKey].getIn(storePath);
      },
      (storeMap, hashid, state, denormalizeFuncFactory) =>
        denormalizeFuncFactory(state),
      (hashid, byId, denormalizeFunc) => {
        const iObject = byId.findLast((obj) => obj.get('hashid') === hashid);
        const object = iObject != null ? iObject.toJS() : undefined;
        return object != null ? denormalizeFunc(object) : object;
      }
    );

    type OwnProps = {|
      ...$Exact<Config>,
    |};

    type StateProps = {|
      fetchedObject?: T,
    |};

    type DispatchProps = {|
      fetchByHashidAction: ({hashid: string}) => any,
    |};

    type Props = {|
      ...OwnProps,
      ...StateProps,
      ...DispatchProps,
    |};

    type State = {|
      loadingFinished: boolean,
      loadingException: any,
    |};

    class ResolverComponent extends React.PureComponent<Props, State> {
      state = {
        loadingFinished: false,
        loadingException: undefined,
      };

      mounted: boolean = false;

      UNSAFE_componentWillMount() {
        this.mounted = true;
        this.fetchObject().catch((err) => {
          //browserHistory.goBack();
          throw err;
        });
      }

      componentWillUnmount() {
        this.mounted = false;
      }

      UNSAFE_componentWillReceiveProps(nextProps) {
        const {hashid} = this.props.params;
        const nextHashid = nextProps.params.hashid;

        if (hashid !== nextHashid) {
          this.fetchObject(nextHashid);
        }
      }

      fetchObject = (hashid?: string) => {
        if (hashid === undefined) {
          hashid = this.props.params.hashid;
        }

        return this.props.fetchByHashidAction({hashid}).then(
          (ret) => {
            if (this.mounted) {
              this.setState({
                loadingFinished: true,
                loadingException: undefined,
              });
            }
            return ret;
          },
          (err) => {
            if (this.mounted) {
              this.setState({
                loadingFinished: true,
                loadingException: err,
              });
            }
            return err;
          }
        );
      };

      render() {
        // eslint-disable-next-line no-unused-vars
        const {fetchedObject, fetchByHashidAction, ...other} = this.props;
        const {loadingFinished, loadingException} = this.state;

        return fetchedObject != null ? (
          <WrappedComponent
            fetchedByHashid={{
              object: fetchedObject,
              fetchObject: this.fetchObject,
            }}
            {...other}
          />
        ) : (
          <LoadingComponent
            {...other}
            isLoading={!loadingFinished}
            error={loadingException}
          />
        );
      }
    }

    const mapStateToProps = (state: StoreShapeType, {params}): StateProps => ({
      fetchedObject: genericStoreFetchSelector(
        storeMap,
        params.hashid,
        state,
        denormalizeFuncFactory
      ),
    });

    return connect<Props, Config, StateProps, DispatchProps, StoreShapeType, _>(
      mapStateToProps,
      {fetchByHashidAction}
    )(ResolverComponent);
  };
}
